import React from 'react';
import { Link } from 'react-router-dom';

// Secondary CTA. 1px Bone Dim stroke, Bone small-caps type. Border lifts to
// Bone on hover. DESIGN.md §6.1 BoneButton. Sharp corners only.
//
// Pass `to` when the button navigates rather than acts, and it renders a
// react-router <Link> wearing the identical chrome. A <button onClick={nav}>
// looks the same and behaves worse: no middle-click, no open-in-new-tab, no
// href for the browser to show. `to` and `onClick` are mutually exclusive by
// convention — if both arrive, `to` wins and the click still fires.
const BoneButton = ({
  children,
  onClick,
  to,
  disabled = false,
  type = 'button',
  trailingIcon,
  leadingIcon,
  fullWidth = false,
  size = 'md',
  className = '',
  ariaLabel,
  'data-testid': testId,
}) => {
  const height = size === 'lg' ? 'h-14' : 'h-12';
  const px = size === 'lg' ? 'px-8' : 'px-7';

  const base = [
    'inline-flex items-center justify-center gap-3',
    'font-display uppercase tracking-[0.10em] text-sm',
    height,
    px,
    'border rounded-field bg-card-sheen',
    'transition-all duration-200 ease-mogged select-none',
    fullWidth ? 'w-full' : '',
    disabled
      ? 'border-vein-dim text-bone-dim cursor-not-allowed'
      : 'border-hairline text-bone hover:border-bone hover:shadow-card-lift active:bg-vein',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {leadingIcon && <span className="inline-flex items-center">{leadingIcon}</span>}
      <span>{children}</span>
      {trailingIcon && <span className="inline-flex items-center">{trailingIcon}</span>}
    </>
  );

  // A disabled link is not a thing the platform has, so `to` + `disabled`
  // degrades to the button — which CAN refuse the click — rather than
  // rendering a live link that merely looks dead.
  if (to && !disabled) {
    return (
      <Link
        to={to}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
        className={`${base} no-underline`}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      className={base}
    >
      {content}
    </button>
  );
};

export default BoneButton;
